import React, { useCallback, useEffect, useState } from 'react';
import { carteraApi } from './api';

const MODOS = [
  { key: 'automatico', label: 'Automático', icon: 'pi-bolt', desc: 'Sale solo a la hora programada. Puedes excluir antes de las 10:00.' },
  { key: 'borrador', label: 'Semiautomático', icon: 'pi-check-square', desc: 'Se prepara solo y espera tu aprobación en la cola.' },
];

const KPI_META = [
  ['vencidos', 'Ya se les pasó', 'pi-exclamation-circle', 'red'],
  ['hoy', 'Vence hoy', 'pi-clock', 'amber'],
  ['proximos', 'Por vencer', 'pi-calendar', 'cyan'],
  ['enviados', 'Enviados', 'pi-check-circle', 'green'],
];

const money = (value) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
}).format(Number(value || 0));

const apiError = (error, fallback) => error?.response?.data?.message
  || Object.values(error?.response?.data?.errors || {})?.flat()?.[0]
  || fallback;

const EstadoMensaje = ({ estado }) => {
  const meta = {
    queued: ['En cola', 'pi-clock', 'amber'], sent: ['Enviado', 'pi-check', 'cyan'],
    delivered: ['Entregado', 'pi-check', 'cyan'], read: ['Leído', 'pi-check-circle', 'green'],
    failed: ['No se entregó', 'pi-times-circle', 'red'],
  }[estado] || [estado || 'Sin estado', 'pi-circle', 'muted'];
  return <span className={`av-status ${meta[2]}`}><i className={`pi ${meta[1]}`} />{meta[0]}</span>;
};

export const CarteraAvisos = ({ onGoConversaciones }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  const cargar = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const response = await carteraApi.getAvisos(signal);
      setData(response.data);
    } catch (err) {
      if (err?.code !== 'ERR_CANCELED') setError(apiError(err, 'No se pudieron cargar los avisos.'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    cargar(controller.signal);
    return () => controller.abort();
  }, [cargar]);

  const ejecutar = async (key, action, fallback) => {
    setBusy(key);
    setNotice(null);
    try {
      const response = await action();
      setNotice({ type: response.data?.status === 'partial' ? 'warn' : 'ok', text: response.data?.message || 'Listo.' });
      await cargar();
    } catch (err) {
      setNotice({ type: 'error', text: apiError(err, fallback) });
    } finally {
      setBusy('');
    }
  };

  const cambiarModo = (modo) => {
    if (modo === data?.modo || busy) return;
    ejecutar(`modo-${modo}`, () => carteraApi.actualizarModoAvisos(modo), 'No se pudo cambiar el modo.');
  };

  const grupos = data?.grupos || [];
  const totalPendientes = grupos.reduce((sum, grupo) => sum + grupo.filas.length, 0);
  const automatico = data?.modo === 'automatico';

  if (loading && !data) return <div className="av-state"><i className="pi pi-spin pi-spinner" /> Cargando avisos…</div>;
  if (error && !data) {
    return (
      <div className="av-state error">
        <i className="pi pi-exclamation-circle" /><b>No se pudieron cargar los avisos</b><span>{error}</span>
        <button type="button" onClick={() => cargar()}>Reintentar</button>
      </div>
    );
  }

  return (
    <section className="av-page">
      <div className="av-title-row">
        <div><h2>Avisos de pago</h2><p>Quién decide que la plantilla salga y el registro de lo enviado.</p></div>
        <button type="button" className="av-primary" onClick={onGoConversaciones}>
          <i className="pi pi-comments" />Ir a Conversaciones <span>{totalPendientes}</span>
        </button>
      </div>

      {automatico && (
        <div className={`av-auto-warning ${data?.pausado ? 'paused' : ''}`}>
          <i className={`pi ${data?.pausado ? 'pi-pause-circle' : 'pi-exclamation-triangle'}`} />
          <div><b>{data?.pausado ? 'Envíos pausados' : 'Nadie revisa antes de enviar'}</b>
            <span>{data?.pausado ? 'Nada saldrá hasta que los reanudes.' : `${totalPendientes} avisos pendientes saldrán desde las ${data?.proximo_envio || '10:00'}.`}</span>
          </div>
          <button type="button" disabled={Boolean(busy)} onClick={() => ejecutar(
            'pausa', () => carteraApi.actualizarPausaAvisos(!data?.pausado), 'No se pudo cambiar la pausa.',
          )}><i className={`pi ${data?.pausado ? 'pi-play' : 'pi-pause'}`} />{data?.pausado ? 'Reanudar envíos' : 'Pausar todos los envíos'}</button>
        </div>
      )}

      {!data?.persistencia_disponible && (
        <div className="av-inline-alert warn"><i className="pi pi-database" />Falta aplicar la migración de ajustes; el selector y la pausa aún no pueden guardarse.</div>
      )}
      {data?.modo === 'manual' && <div className="av-inline-alert warn"><i className="pi pi-info-circle" />El modo anterior sigue en Manual. Elige Semiautomático o Automático para reemplazarlo.</div>}
      {notice && <div className={`av-inline-alert ${notice.type}`}><i className={`pi ${notice.type === 'error' ? 'pi-times-circle' : 'pi-info-circle'}`} />{notice.text}</div>}

      <div className="av-modes">
        {MODOS.map((modo) => {
          const active = data?.modo === modo.key;
          return (
            <button type="button" key={modo.key} className={active ? 'active' : ''} disabled={Boolean(busy)} onClick={() => cambiarModo(modo.key)}>
              <span className="av-mode-icon"><i className={`pi ${modo.icon}`} /></span>
              <span className="av-mode-copy"><b>{modo.label}</b><small>{modo.desc}</small></span>
              <span className="av-mode-dot" />
            </button>
          );
        })}
      </div>

      <div className="av-kpis">
        {KPI_META.map(([key, label, icon, color]) => {
          const kpi = data?.kpis?.[key] || {};
          return (
            <div className="av-kpi" key={key}>
              <span className={`av-kpi-icon ${color}`}><i className={`pi ${icon}`} /></span>
              <div><small>{label}</small><b>{kpi.cantidad || 0}</b><span>{money(kpi.monto)}</span></div>
            </div>
          );
        })}
      </div>

      <div className="av-queue-card">
        <span className="av-queue-icon"><i className="pi pi-comments" /></span>
        <div><b>La cola de avisos vive en Conversaciones</b><span>{totalPendientes} avisos visibles, junto al contexto del cliente y sus pagos por verificar.</span></div>
        <button type="button" onClick={onGoConversaciones}>Abrir Conversaciones <i className="pi pi-arrow-right" /></button>
      </div>

      <div className="av-history-head"><h3>Historial de envíos</h3><p>Qué salió, a qué contrato y quién lo autorizó.</p></div>
      <div className="av-history-card">
        {(data?.historial || []).length === 0 ? (
          <div className="av-history-empty"><i className="pi pi-inbox" />Todavía no hay avisos enviados.</div>
        ) : (
          <div className="av-table-scroll"><table className="av-table"><thead><tr>
            <th>Fecha</th><th>Cliente</th><th>Plantilla</th><th>Contrato</th><th>Estado</th><th>Origen</th>
          </tr></thead><tbody>{data.historial.map((row) => (
            <tr key={row.id}>
              <td className="tnum">{row.fecha}</td><td><b>{row.cliente}</b></td><td>{row.plantilla}</td>
              <td className="tnum">{row.contrato}</td><td><EstadoMensaje estado={row.estado} /></td>
              <td><div className={`av-origin ${row.origen?.tipo}`}><i className={`pi ${row.origen?.tipo === 'automatico' ? 'pi-bolt' : row.origen?.tipo === 'semiautomatico' ? 'pi-check-square' : row.origen?.tipo === 'interno' ? 'pi-bell' : 'pi-user'}`} />
                <span><b>{row.origen?.label}</b><small>{row.origen?.detalle}</small></span></div></td>
            </tr>
          ))}</tbody></table></div>
        )}
      </div>

    </section>
  );
};

export default CarteraAvisos;
